import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { translations } from 'locales/i18n';
import { appDataSelector } from 'app/selectors';
import { useDialogContext } from '../Context';

import { Box, TextField } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { CustomerInterface } from 'app/containers/TechnicalVisitPage/types';
import { FormikInput } from 'app/components/FormikInput';
import { AddressAutocomplete } from 'app/components/AddressAutocomplete';
import { SimpleSelect } from 'app/components/SimpleSelect';
import { SocietyAttributedTo } from 'app/types';
import { upperFirst } from 'lodash';

export const Form = () => {
  const { t } = useTranslation();
  const { setValidationSchema, variant } = useDialogContext();
  const { onSmallDevice, isAdminNovita, isAdmin, isSuperadmin } = useSelector(
    appDataSelector,
  );
  const {
    setFieldValue,
    values: { address, zip, city },
  } = useFormikContext<CustomerInterface>();

  React.useEffect(
    () =>
      setValidationSchema(
        yup.object({
          firstname: yup.string().required(t(translations.formErrors.required)),
          lastname: yup.string().required(t(translations.formErrors.required)),
          address: yup.string().nullable(),
          city: yup.string().nullable(),
          society: yup.string().nullable(),
          zip: yup.string().nullable(),
          coordinates: yup
            .string()
            .matches(
              /^(-?\d+(\.\d+)?),(-?\d+(\.\d+)?)$/,
              t(translations.formErrors.coordinates),
            )
            .nullable(),
          attributedTo: yup
            .string()
            .oneOf(['novita', 'domuneo'])
            .required(t(translations.formErrors.required)),
          phone: yup.string().nullable(),
          mail: yup.string().email(t(translations.formErrors.email)).nullable(),
          mobile: yup.string().nullable(),
        }),
      ),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <>
      <Box display="flex" flexDirection={onSmallDevice ? 'column' : 'row'}>
        <FormikInput
          name="firstname"
          label={t(translations.CustomerForms.labels.firstname)}
          extraAttr={{
            ...variant,
          }}
          containerStyle={{ flex: '2' }}
          component={TextField}
        />
        <FormikInput
          name="lastname"
          label={t(translations.CustomerForms.labels.lastname)}
          extraAttr={{
            ...variant,
          }}
          containerStyle={{ flex: '2' }}
          component={TextField}
        />
      </Box>
      <Box display="flex" flexDirection={onSmallDevice ? 'column' : 'row'}>
        <FormikInput
          name="society"
          label={t(translations.CustomerForms.labels.society)}
          extraAttr={{
            ...variant,
            fullWidth: true,
          }}
          containerStyle={{ flex: '4' }}
          component={TextField}
        />
      </Box>

      <AddressAutocomplete
        defaultValue={address ? `${address} - ${zip}, ${city}` : ''}
        label={t(translations.TechnicalVisitForms.labels.address)}
        manualAddressForm={
          <Box
            display="flex"
            flexDirection="column"
            alignItems="stretch"
            bgcolor="#f0f0f0"
          >
            <FormikInput
              name="address"
              label={t(translations.TechnicalVisitForms.labels.address)}
              extraAttr={{
                variant: 'outlined',
                fullWidth: true,
              }}
              component={TextField}
            />
            <Box display="flex">
              <FormikInput
                name="zip"
                label={t(translations.TechnicalVisitForms.labels.zip)}
                extraAttr={{
                  variant: 'outlined',
                }}
                containerStyle={{ flex: '1' }}
                component={TextField}
              />
              <FormikInput
                name="city"
                label={t(translations.TechnicalVisitForms.labels.city)}
                extraAttr={{
                  variant: 'outlined',
                  fullWidth: true,
                }}
                containerStyle={{ flex: '2' }}
                component={TextField}
              />
            </Box>
            <FormikInput
              name="coordinates"
              label={t(translations.TechnicalVisitForms.labels.coordinates)}
              extraAttr={{
                variant: 'outlined',
                fullWidth: true,
              }}
              component={TextField}
            />
          </Box>
        }
        onAddress={({ address, zip, city, coordinates }) => {
          setFieldValue('address', address);
          setFieldValue('zip', zip);
          setFieldValue('city', city);
          setFieldValue('coordinates', coordinates);
        }}
      />

      <Box display="flex" flexDirection={onSmallDevice ? 'column' : 'row'}>
        <FormikInput
          name="phone"
          label={t(translations.CustomerForms.labels.phone)}
          extraAttr={{
            ...variant,
          }}
          containerStyle={{ flex: '2' }}
          component={TextField}
        />
        <FormikInput
          name="mobile"
          label={t(translations.CustomerForms.labels.mobile)}
          extraAttr={{
            ...variant,
          }}
          containerStyle={{ flex: '2' }}
          component={TextField}
        />
      </Box>
      <Box display="flex" flexDirection={onSmallDevice ? 'column' : 'row'}>
        <FormikInput
          name="mail"
          label={t(translations.CustomerForms.labels.mail)}
          extraAttr={{
            ...variant,
            onChange: (e) =>
              setFieldValue('mail', `${e.target.value}`.toLowerCase()),
          }}
          containerStyle={{ flex: '2' }}
          component={TextField}
        />
        {(isAdmin || isSuperadmin) && !isAdminNovita && (
          <FormikInput
            name="attributedTo"
            label={t(translations.UserForms.labels.attributedTo)}
            extraAttr={{
              variant: 'outlined',
              options: Object.values(SocietyAttributedTo).map((v) => ({
                value: v,
                displayedValue: upperFirst(v),
              })),
              fullWidth: true,
              displayEmpty: false,
            }}
            containerStyle={{ flex: '2' }}
            component={SimpleSelect}
          />
        )}
      </Box>
    </>
  );
};
